import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  getMessageAlert,
  getNotificationAlert,
} from "../../../services/CommonService";
import { IS_CALL } from "../../../configs/AppConfig";
import Utils from "../../../utils";
import MessageAlert from "./MessageAlert";
import NotificationAlert from "./NotificationAlert";
import logo from "../../../assets/Images/Docusafe_logo.webp";
import "./Header.css";
import { toast } from "react-toastify";
import {
  HouseIcon,
  ManageIcon,
  PeopleIcon,
  TemplatesIcon,
  SettingsIcon,
  UserSettingsIcon,
  UsersIcon,
  PreferencesIcon,
  IntegrationIcon,
  BillingIcon,
  HistoryIcon,
  CreditHistoryIcon,
  PricingPlanIcon,
  MessagesIcon,
  NotificationsIcon,
  SidebarIcon,
} from "../../../assets/svgicons/icons";

/**
 * Sidebar Configuration
 * Defines all navigation items, their icons, paths, and sub-menus.
 */
const NavLinks = [
  {
    path: "/client-portal",
    label: "Home",
    icon: <HouseIcon />,
    title: "Home",
  },
  {
    path: "/manage",
    label: "Manage",
    icon: <SettingsIcon />,
    title: "Manage",
  },
  {
    path: "/recipients",
    label: "Collaborator",
    icon: <PeopleIcon />,
    title: "Collaborator",
  },
  {
    path: "/templates",
    label: "Templates",
    icon: <TemplatesIcon />,
    title: "Templates",
  },
  {
    path: "/settings",
    label: "Settings",
    icon: <ManageIcon />,
    title: "Settings",
    isDropdown: true,
    subMenu: [
      { path: "/settings", label: "Account", icon: <UserSettingsIcon /> },
      {
        path: "/settings/users",
        label: "Users",
        permission: "isUserMenu",
        icon: <UsersIcon />,
      },
      {
        path: "/settings/preferences",
        label: "Preferences",
        permission: "isPreferenceMenu",
        icon: <PreferencesIcon />,
      },
      {
        path: "/settings/integration",
        label: "Integration",
        permission: "isIntegrationMenu",
        icon: <IntegrationIcon />,
      },
    ],
  },
  {
    path: "/billing",
    label: "Billing",
    icon: <BillingIcon />,
    title: "Billing",
    isDropdown: true,
    isBillingGroup: true,
    subMenu: [
      { path: "/billing", label: "Billing Overview", icon: <HistoryIcon /> },
      {
        path: "/billing/history",
        label: "Billing History",
        icon: <HistoryIcon />,
      },
      {
        path: "/billing/credit-history",
        label: "Credit History",
        icon: <CreditHistoryIcon />,
      },
      {
        path: "/billing/pricing",
        label: "Pricing Plan",
        icon: <PricingPlanIcon />,
      },
    ],
  },
];

/**
 * Sub-component: SidebarItem
 * Represents a single item in the sidebar, which could be a link or a dropdown.
 */
const SidebarItem = ({
  link,
  isSidebarOpen,
  openDropdown,
  handleDropdownToggle,
  location,
  visibilityMap,
  renderTooltip,
}) => {
  // Respect billing visibility
  if (link.isBillingGroup && !visibilityMap.isShowBilling) return null;

  const isActive = location.pathname.startsWith(link.path);

  const itemContent = (
    <div className="menu-item-content">
      <span className="menu-icon">{link.icon}</span>
      {isSidebarOpen && <span className="menu-label">{link.label}</span>}
      {link.badge && isSidebarOpen && (
        <span className={`menu-badge ${link.badge.toLowerCase()}`}>
          {link.badge}
        </span>
      )}
    </div>
  );

  if (link.isDropdown) {
    const isDropdownOpen = isSidebarOpen && openDropdown === link.path;

    return (
      <li className="nav-menu-item">
        <OverlayTrigger
          placement="right"
          overlay={renderTooltip(isSidebarOpen ? "" : link.title)}
          trigger={isSidebarOpen ? [] : ["hover", "focus"]}
        >
          <button
            className={`menu-dropdown-button ${isActive ? "menu-dropdown-button-active" : "menu-dropdown-button-inactive"}`}
            onClick={() => handleDropdownToggle(link.path)}
          >
            {itemContent}
            {isSidebarOpen && (
              <i
                className={`bi bi-chevron-down dropdown-arrow-sidebar ${isDropdownOpen ? "sidebar-chevron-rotated" : ""}`}
              ></i>
            )}
          </button>
        </OverlayTrigger>

        <div className={`submenu-wrapper ${isDropdownOpen ? "open" : ""}`}>
          <div className="submenu-inner">
            {link.subMenu.map((subItem, index) => {
              // Safe visibility check without eval()
              const isVisible = subItem.permission ? visibilityMap[subItem.permission] : true;
              if (!isVisible) return null;

              return (
                <NavLink
                  key={index}
                  to={subItem.path}
                  className={({ isActive }) =>
                    `submenu-link ${isActive ? "submenu-link-active" : "submenu-link-inactive"}`
                  }
                >
                  <span className="submenu-icon">{subItem.icon}</span>
                  <span className="submenu-label">{subItem.label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="nav-menu-item">
      <OverlayTrigger
        placement="right"
        overlay={renderTooltip(isSidebarOpen ? "" : link.title)}
        trigger={isSidebarOpen ? [] : ["hover", "focus"]}
      >
        <NavLink
          className={`nav-menu-link ${isActive ? "nav-menu-link-active" : "nav-menu-link-inactive"}`}
          to={link.path}
        >
          {itemContent}
        </NavLink>
      </OverlayTrigger>
    </li>
  );
};

/**
 * Main Header Component
 */
function Header({ isSidebarOpen, setIsSidebarOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  
  // User & Account details
  const [companyName, setCompanyName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loginUserName, setLoginUserName] = useState("");
  const [loginUserEmail, setLoginUserEmail] = useState("");
  
  // Alert Status
  const [totalNotification, setTotalNotification] = useState(0);
  const [notificationList, setNotificationList] = useState([]);
  const [totalMessage, setTotalMessage] = useState(0);
  const [messageAlertList, setMessageAlertList] = useState([]);
  
  // Permission Visibility Map
  const [visibilityMap, setVisibilityMap] = useState({
    isShowBilling: true,
    isUserMenu: true,
    isPreferenceMenu: true,
    isIntegrationMenu: true,
  });

  const [openDropdown, setOpenDropdown] = useState(null);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userIconRef = useRef(null);
  const userDropdownRef = useRef(null);

  const messageAlertCalled = useRef(false);
  const notificationAlertCalled = useRef(false);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  /**
   * Tooltip Helper
   */
  const renderTooltip = (text) => {
    if (!text) return <span style={{ display: "none" }} />;
    return (
      <Tooltip id="sidebar-tooltip" className="sidebar-custom-tooltip">
        {text}
      </Tooltip>
    );
  };

  /**
   * Session / Idle Handling
   */
  const IDLE_TIMEOUT = 12 * 60 * 60 * 1000;
  let idleTimer = null;

  const resetIdleTimer = () => {
    clearTimeout(idleTimer);
    Utils.updateLastActivity();
    idleTimer = setTimeout(() => {
      toast.info("Session timed out. Please log in again.");
      Utils.logout();
    }, IDLE_TIMEOUT);
  };

  useEffect(() => {
    if (Utils.checkSessionExpiry()) return;
    if (!localStorage.getItem("lastActivity")) Utils.updateLastActivity();

    resetIdleTimer();
    const events = ["mousemove", "keydown", "touchstart", "scroll"];
    events.forEach((event) => window.addEventListener(event, resetIdleTimer));

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetIdleTimer));
      clearTimeout(idleTimer);
    };
  }, []);

  /**
   * Dropdown Mechanics
   */
  const handleDropdownToggle = (menu) => {
    if (!isSidebarOpen) setIsSidebarOpen(true);
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  /**
   * API Calls - Notifications & Messages
   */
  const fetchNotifications = () => {
    getNotificationAlert({})
      .then((res) => {
        setTotalNotification(res.data.data.length);
        setNotificationList(res.data.data);
      })
      .catch(() => {});
  };

  const fetchMessages = () => {
    getMessageAlert({ entity_type: 1 })
      .then((res) => {
        setTotalMessage(res.data.data.length);
        setMessageAlertList(res.data.data);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (notificationAlertCalled.current) return;
    notificationAlertCalled.current = true;
    fetchNotifications();
    if (IS_CALL) {
      const interval = setInterval(fetchNotifications, 10000);
      return () => clearInterval(interval);
    }
  }, []);

  useEffect(() => {
    if (messageAlertCalled.current) return;
    messageAlertCalled.current = true;
    fetchMessages();
    if (IS_CALL) {
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, []);

  /**
   * Profile Initialization
   */
  useEffect(() => {
    setDisplayName(Utils.displayName());
    const loginData = Utils.loginUserData();
    if (Object.keys(loginData).length > 0) {
      setLoginUserName(`${loginData.first_name} ${loginData.last_name}`);
      setLoginUserEmail(loginData.email);
      setCompanyName(loginData.company_name);
      
      // Update visibility permissions based on role
      if (loginData.role_id > 2) {
        setVisibilityMap(prev => ({ ...prev, isShowBilling: false }));
      }
    }
  }, []);

  /**
   * Outside Click Handling
   */
  useEffect(() => {
    if (!isUserDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (
        userDropdownRef.current && !userDropdownRef.current.contains(event.target) &&
        userIconRef.current && !userIconRef.current.contains(event.target)
      ) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isUserDropdownOpen]);

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
    if (!isUserDropdownOpen) setOpenDropdown(null);
  };

  return (
    <>
      <div className="nav-container">
        <div className={`nav-sidebar pt-3 ${isSidebarOpen ? "sidebar-expanded" : "sidebar-collapsed"}`}>
          
          {/* Top Section: Logo & Toggle */}
          <div className={`sidebar-top-section ${isSidebarOpen ? "sidebar-top-expanded" : ""}`}>
            {isSidebarOpen && (
              <img src={logo} alt="Docusafe" className="sidebar-main-logo" />
            )}
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip(isSidebarOpen ? "" : "Expand")}
              trigger={isSidebarOpen ? [] : ["hover", "focus"]}
            >
              <button onClick={toggleSidebar} className="sidebar-toggle-button">
                <SidebarIcon width={20} height={20} color="white" />
              </button>
            </OverlayTrigger>
          </div>

          {/* Menu Section */}
          <ul className="nav-menu-list">
            <div className="menu-section">
              {NavLinks.map((link, index) => (
                <SidebarItem
                  key={index}
                  link={link}
                  isSidebarOpen={isSidebarOpen}
                  openDropdown={openDropdown}
                  handleDropdownToggle={handleDropdownToggle}
                  location={location}
                  visibilityMap={visibilityMap}
                  renderTooltip={renderTooltip}
                />
              ))}
            </div>
          </ul>

          {/* Bottom Section: Notifications & Profile */}
          <div className={`sidebar-bottom-section ${isSidebarOpen ? "bottom-section-horizontal" : "bottom-section-vertical"}`}>
            
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip(isSidebarOpen ? "" : "Messages")}
              trigger={isSidebarOpen ? [] : ["hover", "focus"]}
            >
              <div className="notification-icon-button" data-bs-toggle="offcanvas" data-bs-target="#message_box">
                <div style={{ position: "relative" }}>
                  <MessagesIcon />
                  {totalMessage > 0 && (
                    <span className="message-count-badge">{totalMessage}</span>
                  )}
                </div>
              </div>
            </OverlayTrigger>

            <OverlayTrigger
              placement="right"
              overlay={renderTooltip(isSidebarOpen ? "" : "Notifications")}
              trigger={isSidebarOpen ? [] : ["hover", "focus"]}
            >
              <div className="notification-icon-button" data-bs-toggle="offcanvas" data-bs-target="#notification_box">
                <div style={{ position: "relative" }}>
                  <NotificationsIcon />
                  {totalNotification > 0 && (
                    <span className="notification-count-badge">{totalNotification}</span>
                  )}
                </div>
              </div>
            </OverlayTrigger>

            {/* Profile Avatar & Dropdown */}
            <div ref={userIconRef} style={{ position: "relative" }}>
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip(isSidebarOpen ? "" : (displayName || "User Profile"))}
                trigger={isSidebarOpen ? [] : ["hover", "focus"]}
              >
                <span className="user-profile-avatar" onClick={toggleUserDropdown}>
                  {displayName}
                </span>
              </OverlayTrigger>

              {isUserDropdownOpen && (
                <div ref={userDropdownRef} className="user-profile-dropdown" style={{ left: isSidebarOpen ? "170px" : "30px" }}>
                  <div className="user-profile-header ">
                    <span>{companyName}</span>
                    <span className="company-name-badge">{displayName}</span>
                  </div>
                  <div className="user-profile-details text-white">
                    <p style={{ margin: 0 }}>
                      <span className="d-block text-md">{loginUserName}</span>
                      <span className="d-block text-md">{loginUserEmail}</span>
                      <span className="d-block mt-1">
                        <a href="https://app.docusafe.ai/login" className="propage-nav-link text-decoration-none text-white" target="_blank" rel="noopener noreferrer">
                          Docusafe
                        </a>
                      </span>
                    </p>
                  </div>
                  <div className="logout-button" onClick={() => { setIsUserDropdownOpen(false); Utils.logout(); }}>
                    Sign Out
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* External Alert Modals */}
      <MessageAlert
        messageAlertList={messageAlertList}
        setMessageAlertList={setMessageAlertList}
        setTotalMessage={setTotalMessage}
        setLoading={setLoading}
      />
      <NotificationAlert
        notificationList={notificationList}
        setNotificationList={setNotificationList}
        setTotalNotification={setTotalNotification}
        setLoading={setLoading}
      />
    </>
  );
}

export default Header;
